import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { SessionProvider } from './session/SessionProvider';
import { BrandingProvider } from './branding/BrandingProvider';
import PortalBrowserMetadata from './branding/PortalBrowserMetadata';
import { useSessionStore } from './session/store';
import { startAppEventSocket, stopAppEventSocket } from './store/appEventStore';
import AppShell from './components/layout/AppShell';

import DashboardPage       from './pages/user/Dashboard';
import StrategiesPage      from './pages/user/Strategies';
import StrategyCreatePage  from './pages/user/StrategyCreate';
import StrategyBuilderPage from './pages/user/StrategyBuilder';
import SignalsPage         from './pages/user/Signals';
import ManualTradingPage   from './pages/user/manual-trading/ManualTradingPage';
import RuntimeLogsPage     from './pages/user/RuntimeLogs';

import AdminOverviewPage from './pages/admin/AdminOverview';
import AdminUsersPage    from './pages/admin/AdminUsers';
import AdminRuntimePage  from './pages/admin/AdminRuntime';
import AdminDispatchLogs      from './pages/admin/AdminDispatchLogs';
import AdminCandleInspector   from './pages/admin/AdminCandleInspector';
import AdminMarketCalendar    from './pages/admin/AdminMarketCalendar';
import AdminUserStrategies    from './pages/admin/AdminUserStrategies';
import AdminTradingAudit     from './pages/admin/AdminTradingAudit';

import OptionsHomePage          from './pages/options/OptionsHomePage';
import OptionComposerPage       from './pages/options/OptionComposerPage';
import OptionStrategyDetailPage from './pages/options/OptionStrategyDetailPage';
import OptionDeployPage         from './pages/options/OptionDeployPage';

// Public marketing / funnel pages
import TradingViewLandingPage from './pages/public/tradingview-landing/TradingViewLandingPage';
import ThankYouPage           from './pages/public/tradingview-landing/ThankYouPage';

// Phase 1 — Identity integration pages
import LoginPage           from './pages/auth/LoginPage';
import RegisterPage        from './pages/auth/RegisterPage';
import VerifyEmailPage     from './pages/auth/VerifyEmailPage';
import ForgotPasswordPage  from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage   from './pages/auth/ResetPasswordPage';
import ChangePasswordPage  from './pages/auth/ChangePasswordPage';
import ExtensionConnectPage from './pages/extension/ExtensionConnectPage';
import ProfilePage         from './pages/user/ProfilePage';
import BrokerPage          from './pages/user/BrokerPage';
import BrokerOrderLogPage  from './pages/broker/BrokerOrderLogPage';
import PortfolioPage       from './pages/broker/PortfolioPage';
import SubscriptionPage           from './pages/user/SubscriptionPage';
import OperatorPlansPage          from './pages/user/OperatorPlansPage';
import OperatorRechargePage       from './pages/user/OperatorRechargePage';
import OperatorPaymentSettingsPage from './pages/user/OperatorPaymentSettingsPage';
import OperatorBrandingSettingsPage from './pages/user/OperatorBrandingSettingsPage';
import AdminUserDetailPage        from './pages/admin/AdminUserDetailPage';
import AdminPlansPage             from './pages/admin/AdminPlansPage';
import AdminLicensePacksPage      from './pages/admin/AdminLicensePacksPage';
import AdminSubscriptionPlansPage from './pages/admin/AdminSubscriptionPlansPage';
import AdminProxyPage             from './pages/admin/AdminProxyPage';
import AdminWebsiteLeads         from './pages/admin/AdminWebsiteLeads';
import AdminWebsiteArticles      from './pages/admin/AdminWebsiteArticles';
import AdminPayments             from './pages/admin/AdminPayments';
import AdminGoogleReviews        from './pages/admin/AdminGoogleReviews';

import { ROUTES } from './routes/constants';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 2, staleTime: 15_000, refetchOnWindowFocus: false },
  },
});

const THEME_KEY = 'algosys-theme';

function AuthThemeGuard({ children }) {
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', 'dark');
    return () => {
      const saved = localStorage.getItem(THEME_KEY);
      document.documentElement.setAttribute('data-theme', saved === 'light' || saved === 'dark' ? saved : 'dark');
    };
  }, []);

  return children;
}

// AuthGuard: redirects to /login when in standalone-token mode and not authenticated.
// No-op in dev-open and algosys-embedded modes.
function AuthGuard({ children }) {
  const { mode, ready, token, mustChangePassword } = useSessionStore();
  const location = useLocation();
  if (mode === 'standalone-token' && ready && !token) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }
  if (
    mode === 'standalone-token'
    && ready
    && token
    && mustChangePassword
    && location.pathname !== ROUTES.CHANGE_PASSWORD
  ) {
    return <Navigate to={ROUTES.CHANGE_PASSWORD} replace />;
  }
  return children;
}

function RoleGuard({ children }) {
  const { role, isSuperAdmin } = useSessionStore();
  if (role !== 'admin' && role !== 'operator' && !isSuperAdmin) {
    return <Navigate to={ROUTES.STRATEGIES} replace />;
  }
  return children;
}

// Exact Operator role only — ordinary customers, Admins and Super Admins are
// redirected away. The backend stays the authorization authority for the
// Operator-owned payment and branding APIs these pages call.
function OperatorGuard({ children }) {
  const role = useSessionStore((s) => s.role);
  if (role !== 'operator') return <Navigate to={ROUTES.STRATEGIES} replace />;
  return children;
}

function SuperAdminGuard({ children }) {
  const { isSuperAdmin } = useSessionStore();
  if (!isSuperAdmin) return <Navigate to={ROUTES.ADMIN_OVERVIEW} replace />;
  return children;
}


function AppEventSocketBridge() {
  const queryClient = useQueryClient();
  const ready = useSessionStore((s) => s.ready);
  const token = useSessionStore((s) => s.token ?? null);
  const mode = useSessionStore((s) => s.mode);

  useEffect(() => {
    if (ready && (token || mode === 'dev-open')) {
      startAppEventSocket(queryClient, { allowTokenless: mode === 'dev-open' });
      return () => stopAppEventSocket();
    }
    stopAppEventSocket();
    return undefined;
  }, [ready, token, mode, queryClient]);

  return null;
}

function SessionQueryBoundary({ children }) {
  const queryClient = useQueryClient();
  const userId = useSessionStore((s) => s.user?.id ?? null);
  const token = useSessionStore((s) => s.token ?? null);
  const authKey = token ? `${userId ?? 'unknown'}:${token}` : 'guest';
  const prevAuthKeyRef = useRef(authKey);

  useEffect(() => {
    if (prevAuthKeyRef.current !== authKey) {
      queryClient.clear();
      prevAuthKeyRef.current = authKey;
    }
  }, [authKey, queryClient]);

  return children;
}

export default function App() {
  return (
    // BrandingProvider sits outside the query client and session tree: branding is
    // decorative presentation state, must not live in the auth-scoped query cache,
    // and must never gate session bootstrap.
    <BrandingProvider>
    <QueryClientProvider client={queryClient}>
      <SessionQueryBoundary>
        <SessionProvider>
          <AppEventSocketBridge />
          <BrowserRouter>
            <PortalBrowserMetadata />
            <Routes>
            {/* ── Public marketing / funnel routes (outside AppShell, no guard, no auth) ── */}
            <Route path={ROUTES.TRADINGVIEW_LANDING}           element={<TradingViewLandingPage />} />
            <Route path={ROUTES.TRADINGVIEW_LANDING_THANK_YOU} element={<ThankYouPage />} />

            {/* ── Auth routes (outside AppShell, no guard needed) ── */}
            <Route path={ROUTES.LOGIN}           element={<AuthThemeGuard><LoginPage /></AuthThemeGuard>} />
            <Route path={ROUTES.SIGNUP}          element={<AuthThemeGuard><RegisterPage /></AuthThemeGuard>} />
            <Route path={ROUTES.VERIFY}          element={<AuthThemeGuard><VerifyEmailPage /></AuthThemeGuard>} />
            <Route path={ROUTES.FORGOT_PASSWORD} element={<AuthThemeGuard><ForgotPasswordPage /></AuthThemeGuard>} />
            <Route path={ROUTES.RESET_PASSWORD}  element={<AuthThemeGuard><ResetPasswordPage /></AuthThemeGuard>} />

            {/* ── Chrome extension secure connect flow (Phase 2F) — requires login, but is
                 not part of the AppShell/RoleGuard chrome ── */}
            <Route path={ROUTES.EXTENSION_CONNECT} element={<ExtensionConnectPage />} />

              {/* ── App routes (inside AppShell, guarded) ── */}
              <Route element={<AuthGuard><AppShell /></AuthGuard>}>
                <Route index element={<Navigate to={ROUTES.STRATEGIES} replace />} />

                <Route path={ROUTES.DASHBOARD}       element={<DashboardPage />} />
                <Route path={ROUTES.STRATEGIES}      element={<StrategiesPage />} />
                <Route path={ROUTES.STRATEGIES_CREATE} element={<StrategyCreatePage />} />
                <Route path={ROUTES.STRATEGIES_NEW}  element={<StrategyBuilderPage />} />
                <Route path={ROUTES.STRATEGY_EDIT}   element={<StrategyBuilderPage />} />
                <Route path={ROUTES.SIGNALS}         element={<SignalsPage />} />
                <Route path={ROUTES.MANUAL_TRADING}  element={<ManualTradingPage />} />
                <Route path={ROUTES.LOGS}            element={<RuntimeLogsPage />} />

                <Route path={ROUTES.PROFILE}          element={<ProfilePage />} />
                <Route path={ROUTES.BROKER}           element={<BrokerPage />} />
                <Route path={ROUTES.BROKER_ORDERS}    element={<BrokerOrderLogPage />} />
                <Route path={ROUTES.BROKER_ORDERBOOK} element={<Navigate to={ROUTES.PORTFOLIO_ORDERS} replace />} />
                <Route path={ROUTES.BROKER_POSITIONS} element={<Navigate to={ROUTES.PORTFOLIO_POSITIONS} replace />} />
                <Route path={ROUTES.BROKER_HOLDINGS}  element={<Navigate to={ROUTES.PORTFOLIO_HOLDINGS} replace />} />
                <Route path={ROUTES.PORTFOLIO}           element={<Navigate to={ROUTES.PORTFOLIO_POSITIONS} replace />} />
                <Route path={ROUTES.PORTFOLIO_ORDERS}    element={<PortfolioPage />} />
                <Route path={ROUTES.PORTFOLIO_POSITIONS} element={<PortfolioPage />} />
                <Route path={ROUTES.PORTFOLIO_HOLDINGS}  element={<PortfolioPage />} />
                <Route path={ROUTES.SUBSCRIPTION}     element={<SubscriptionPage />} />
                <Route path={ROUTES.OPERATOR_PLANS}   element={<OperatorPlansPage />} />
                <Route path={ROUTES.OPERATOR_RECHARGE} element={<OperatorRechargePage />} />
                <Route path={ROUTES.OPERATOR_PAYMENT_SETTINGS} element={<OperatorGuard><OperatorPaymentSettingsPage /></OperatorGuard>} />
                <Route path={ROUTES.OPERATOR_BRANDING_SETTINGS} element={<OperatorGuard><OperatorBrandingSettingsPage /></OperatorGuard>} />
                <Route path={ROUTES.CHANGE_PASSWORD}  element={<ChangePasswordPage />} />

                <Route path={ROUTES.OPTIONS}         element={<OptionsHomePage />} />
                <Route path={ROUTES.OPTIONS_NEW}     element={<OptionComposerPage />} />
                <Route path={ROUTES.OPTIONS_DEPLOY}  element={<OptionDeployPage />} />
                <Route path={ROUTES.OPTIONS_DETAIL}  element={<OptionStrategyDetailPage />} />

                <Route path={ROUTES.ADMIN_OVERVIEW}  element={<RoleGuard><AdminOverviewPage /></RoleGuard>} />
                <Route path={ROUTES.ADMIN_USERS}           element={<RoleGuard><AdminUsersPage /></RoleGuard>} />
                <Route path={ROUTES.ADMIN_USER_DETAIL}     element={<RoleGuard><AdminUserDetailPage /></RoleGuard>} />
                <Route path={ROUTES.ADMIN_USER_STRATEGIES} element={<RoleGuard><AdminUserStrategies /></RoleGuard>} />
                <Route path={ROUTES.ADMIN_RUNTIME}         element={<RoleGuard><AdminRuntimePage /></RoleGuard>} />
                <Route path={ROUTES.ADMIN_DISPATCH}  element={<RoleGuard><AdminDispatchLogs /></RoleGuard>} />
                <Route path={ROUTES.ADMIN_CANDLES}          element={<RoleGuard><AdminCandleInspector /></RoleGuard>} />
                <Route path={ROUTES.ADMIN_MARKET_CALENDAR} element={<RoleGuard><AdminMarketCalendar /></RoleGuard>} />
                <Route path={ROUTES.ADMIN_PLANS}                element={<Navigate to={ROUTES.ADMIN_LICENSE_PACKS} replace />} />
                <Route path={ROUTES.ADMIN_LICENSE_PACKS}        element={<RoleGuard><AdminLicensePacksPage /></RoleGuard>} />
                <Route path={ROUTES.ADMIN_SUBSCRIPTION_PLANS}   element={<RoleGuard><AdminSubscriptionPlansPage /></RoleGuard>} />
                <Route path={ROUTES.ADMIN_PROXY}               element={<RoleGuard><AdminProxyPage /></RoleGuard>} />
                <Route path={ROUTES.ADMIN_TRADING_AUDIT}      element={<RoleGuard><AdminTradingAudit /></RoleGuard>} />
                <Route path={ROUTES.ADMIN_WEBSITE_LEADS}      element={<RoleGuard><SuperAdminGuard><AdminWebsiteLeads /></SuperAdminGuard></RoleGuard>} />
                <Route path={ROUTES.ADMIN_WEBSITE_ARTICLES}   element={<RoleGuard><SuperAdminGuard><AdminWebsiteArticles /></SuperAdminGuard></RoleGuard>} />
                <Route path={ROUTES.ADMIN_PAYMENTS}           element={<RoleGuard><SuperAdminGuard><AdminPayments /></SuperAdminGuard></RoleGuard>} />
                <Route path={ROUTES.ADMIN_GOOGLE_REVIEWS}     element={<RoleGuard><SuperAdminGuard><AdminGoogleReviews /></SuperAdminGuard></RoleGuard>} />

                {/* Legacy redirects */}
                <Route path="/deployments"    element={<Navigate to={ROUTES.STRATEGIES} replace />} />
                <Route path="/deployments/*"  element={<Navigate to={ROUTES.STRATEGIES} replace />} />
                <Route path="/alerts"         element={<Navigate to={ROUTES.STRATEGIES} replace />} />
                <Route path="/admin/health"   element={<Navigate to={ROUTES.ADMIN_RUNTIME} replace />} />

                <Route path="*" element={<Navigate to={ROUTES.STRATEGIES} replace />} />
              </Route>
            </Routes>
          </BrowserRouter>
        </SessionProvider>
      </SessionQueryBoundary>
    </QueryClientProvider>
    </BrandingProvider>
  );
}
